import { FC, useMemo } from "react";

import { Pager, DataRow, DataSource, Paged, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";

import { PAResultPanel, PAResultPanelProps } from "./PAResultPanel";

interface PAResultListProps {
  results: PAResultPanelProps["data"][];
  page: number;
  setPage: (page: number) => void;
  singleMember?: boolean;
  patientDOB?:string;
  backUrl?: string;
}

const RESULTS_PER_PAGE = 10;

const PAResultList: FC<PAResultListProps> = ({ results, page, setPage, singleMember, patientDOB, backUrl }) => {
  const allRows: ReadonlyArray<DataRow<PAResultPanelProps["data"]>> = useMemo(
    () =>
      results.map((item, index) => ({
        id: index,
        data: item,
      })),
    [results],
  );

  const pagedRows = useMemo(() => {
    const start = page * RESULTS_PER_PAGE;
    return allRows.slice(start, start + RESULTS_PER_PAGE);
  }, [allRows, page]);

  const baseDataSource = useSimpleTableDataSource<PAResultPanelProps["data"]>(pagedRows);

  const dataSource: DataSource<object> & Paged = {
    ...baseDataSource,
    page: page,
    pageSize: RESULTS_PER_PAGE,
    totalRows: allRows.length,
    maxRows: allRows.length,
    hasNextPage: (page + 1) * RESULTS_PER_PAGE < allRows.length,
    first: () => setPage(1),
    previous: () => setPage(Math.max(page - 1, 0)),
    next: () => {
      const nextPage = (page + 1) * RESULTS_PER_PAGE < allRows.length ? page + 1 : page;
      setPage(nextPage);
    },
    last: () => setPage(Math.floor((allRows.length - 1) / RESULTS_PER_PAGE)),
  };

  return (
    <>
      {pagedRows.map((row) => (
        <PAResultPanel key={row.id} data={row.data} singleMember={singleMember} patientDOB={patientDOB} backUrl={backUrl} />
      ))}
      {allRows.length > 0 && <Pager dataSource={dataSource} />}
    </>
  );
};

export default PAResultList;
